import { apiClient } from './apiClient';
import { ApiRequestOptions, UserProfile } from '../types/api';
import { resolveAvatarUrl } from '../constants/assets';

export interface UserSearchResult {
    userId: string;
    displayName: string;
    username: string;
    avatarUrl?: string;
    verified: boolean;
}

export interface UserCredentialInfo {
    credentialType: string;
    credentialValue: string;
    hasPassword: boolean;
    isVerified: boolean;
    status: string;
}

const notifyProfileUpdated = (detail?: Partial<UserProfile>) => {
    window.dispatchEvent(new CustomEvent('profile-updated', { detail }));
};

const normalizeUserProfile = (profile: UserProfile): UserProfile => ({
    ...profile,
    avatarUrl: resolveAvatarUrl(profile.avatarUrl)
});

const normalizeAvatarRecord = <T extends { avatarUrl?: string }>(record: T): T & { avatarUrl: string } => ({
    ...record,
    avatarUrl: resolveAvatarUrl(record.avatarUrl)
});

/**
 * Service to handle user-related API calls.
 */
export const userService = {
    /**
     * Get the current user's profile.
     * Endpoint: GET /user/me
     */
    getUserProfile: async (options?: ApiRequestOptions) => {
        const response = await apiClient.get<{ userProfile: UserProfile }>('/api/v1/user/me', undefined, options);
        if (response.success && response.data?.userProfile) {
            return {
                ...response,
                data: {
                    ...response.data,
                    userProfile: normalizeUserProfile(response.data.userProfile)
                }
            };
        }
        return response;
    },

    /**
     * Get the current user's credentials.
     * Endpoint: GET /user/me/credentials
     */
    getUserCredentials: async () => {
        return apiClient.get<{ credentials: UserCredentialInfo[] }>('/api/v1/user/me/credentials');
    },

    /**
     * Send an SMS verification code for setting or changing the current user's password.
     * Endpoint: POST /user/me/password/code
     */
    sendPasswordVerificationCode: async () => {
        return apiClient.post<{ message: string; phone?: string }>('/api/v1/user/me/password/code');
    },

    /**
     * Send an SMS verification code for scheduling account deletion.
     * Endpoint: POST /user/me/account-deletion/code
     */
    sendAccountDeletionVerificationCode: async () => {
        return apiClient.post<{ message: string; effectiveAt?: number }>('/api/v1/user/me/account-deletion/code');
    },

    /**
     * Set or change the current user's password after SMS verification.
     * Endpoint: POST /user/me/password
     */
    updatePassword: async (password: string, verificationCode: string) => {
        return apiClient.post<{ message: string }>('/api/v1/user/me/password', { password, verificationCode });
    },

    /**
     * Schedule account deletion after SMS verification.
     * Endpoint: POST /user/me/account-deletion
     */
    requestAccountDeletion: async (verificationCode: string) => {
        return apiClient.post<{ message: string; effectiveAt: number }>('/api/v1/user/me/account-deletion', { verificationCode });
    },

    /**
     * Get another user's profile by ID.
     */
    getUserProfileById: async (userId: string) => {
        const response = await apiClient.get<{ userProfile: UserProfile }>(`/api/v1/user/${encodeURIComponent(userId)}`);
        if (response.success && response.data?.userProfile) {
            return {
                ...response,
                data: {
                    ...response.data,
                    userProfile: normalizeUserProfile(response.data.userProfile)
                }
            };
        }
        return response;
    },

    /**
     * Update the current user's profile.
     * Endpoint: PUT /user/me/profile
     */
    updateUserProfile: async (data: Partial<UserProfile>) => {
        const response = await apiClient.put<{ success: boolean }>('/api/v1/user/me/profile', data);
        if (response.success) {
            notifyProfileUpdated(data);
        }
        return response;
    },

    /**
     * Mark the first-login onboarding tour as completed/dismissed.
     * Endpoint: POST /user/me/onboarding-complete
     */
    completeOnboarding: async () => {
        return apiClient.post<{ success: boolean }>('/api/v1/user/me/onboarding-complete', {});
    },

    /**
     * Update avatar URL on the user profile.
     */
    updateAvatar: async (avatarUrl: string): Promise<{ success: boolean; avatarUrl?: string; error?: string }> => {
        try {
            const response = await userService.updateUserProfile({ avatarUrl });

            if (response.success) {
                const profileResponse = await userService.getUserProfile();
                if (profileResponse.success && profileResponse.data?.userProfile?.avatarUrl) {
                    return { success: true, avatarUrl: profileResponse.data.userProfile.avatarUrl };
                }

                return { success: true };
            }
            return { success: false, error: response.error || '更新头像失败' };
        } catch (error: any) {
            console.error('Avatar update failed:', error);
            return { success: false, error: error.message || '更新头像失败' };
        }
    },

    /**
     * Update cover image by sending coverImageUrl to profile update API.
     * Uses the PUT /user/me/profile endpoint with coverImageUrl field.
     * @param coverDataUrl - Base64 data URL of the cropped cover image
     */
    updateCoverImage: async (coverDataUrl: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await userService.updateUserProfile({ coverImageUrl: coverDataUrl });

            if (response.success) {
                return { success: true };
            }
            return { success: false, error: response.error || '更新封面失败' };
        } catch (error: any) {
            console.error('Cover image update failed:', error);
            return { success: false, error: error.message || '更新封面失败' };
        }
    },

    /**
     * Follow a user
     */
    followUser: async (userId: string): Promise<{ success: boolean; message?: string }> => {
        return apiClient.post<{ success: boolean; message?: string }>(`/api/v1/user/${userId}/follow`);
    },

    /**
     * Unfollow a user
     */
    unfollowUser: async (userId: string): Promise<{ success: boolean; message?: string }> => {
        return apiClient.delete<{ success: boolean; message?: string }>(`/api/v1/user/${userId}/follow`);
    },

    /**
     * Get following status for a specific user
     */
    getFollowingStatus: async (userId: string, options?: ApiRequestOptions): Promise<{ isFollowing: boolean }> => {
        const result = await apiClient.get<{ isFollowing: boolean }>(`/api/v1/user/${userId}/followStatus`, undefined, options);
        if (result.success && result.data) {
            return { isFollowing: result.data.isFollowing };
        }
        return { isFollowing: false }; // Default on error
    },

    /**
     * Get followers
     */
    getFollowers: async (userId?: string, options?: ApiRequestOptions): Promise<{ followers: any[] }> => {
        if (!userId) {
            // To properly fetch "my" followers if userId is empty, we must fetch /user/me first, then call /id/followers. 
            // Normally /me/followers is a separate endpoint, but we didn't add it.
            // We return [] safely here if no userId provided and no easy /me route. Let UI pass it explicitly.
            console.warn("getFollowers called without userId, returning empty");
            return { followers: [] };
        }
        const result = await apiClient.get<{ followers: any[] }>(`/api/v1/user/${userId}/followers`, undefined, options);
        if (result.success && result.data) {
            return {
                followers: (result.data.followers || []).map(normalizeAvatarRecord)
            };
        }
        return { followers: [] };
    },

    /**
     * Get following
     */
    getFollowing: async (userId?: string, options?: ApiRequestOptions): Promise<{ following: any[] }> => {
        if (!userId) {
            console.warn("getFollowing called without userId, returning empty");
            return { following: [] };
        }
        const result = await apiClient.get<{ following: any[] }>(`/api/v1/user/${userId}/following`, undefined, options);
        if (result.success && result.data) {
            return {
                following: (result.data.following || []).map(normalizeAvatarRecord)
            };
        }
        return { following: [] };
    },

    /**
     * Get avatars in batch for a list of user IDs
     * Endpoint: POST /api/v1/user/avatars
     */
    getAvatarsBatch: async (userIds: string[]) => {
        if (!userIds || userIds.length === 0) return { success: true, data: { avatars: {} } };
        const response = await apiClient.post<{ avatars: Record<string, { avatarUrl: string; nickname: string }> }>('/api/v1/user/avatars', { userIds });
        if (response.success && response.data?.avatars) {
            const avatars = Object.fromEntries(
                Object.entries(response.data.avatars).map(([userId, profile]) => [
                    userId,
                    normalizeAvatarRecord(profile)
                ])
            );
            return {
                ...response,
                data: {
                    ...response.data,
                    avatars
                }
            };
        }
        return response;
    },

    /**
     * Search users by nickname, username, or user ID
     * Endpoint: GET /api/v1/user/search?q=keyword&limit=8
     */
    searchUsers: async (query: string, limit: number = 8) => {
        const trimmed = query.trim();
        if (!trimmed) {
            return { success: true, data: { users: [] as UserSearchResult[] } };
        }

        const queryParams = new URLSearchParams({
            q: trimmed,
            limit: String(limit)
        });

        const response = await apiClient.get<{ users: UserSearchResult[] }>(`/api/v1/user/search?${queryParams.toString()}`);
        if (response.success && response.data?.users) {
            return {
                ...response,
                data: {
                    ...response.data,
                    users: response.data.users.map(normalizeAvatarRecord)
                }
            };
        }
        return response;
    },

    getUserStats: (userId: string) => {
        return apiClient.get<{ userId: string; totalLikes: number; reviewCount: number; ratingSum: number; averageRating: number }>(`/api/v1/user/${userId}/stats`);
    }
};
